import { AssetRelationType, assetRelationTypes, ParsedAsset } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { AnimatedLegendList } from '@legendapp/list/reanimated'
import { useCallback, useMemo, useState } from 'react'
import { View } from 'react-native'
import { useSpinDelay } from 'spin-delay'
import { useDebounce } from 'use-debounce'
import { PropertyListItem } from '~/components/assets/PropertyListItem'
import { PropertyTypeFilter } from '~/components/assets/PropertyTypeFilter'
import Icon from '~/components/Icon'
import { Heading1 } from '~/components/typography/Titles'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { useAssetsPaginated, type AssetsManagementFilterKey } from '~/data/assets/useAssets'
import { cn } from '~/lib/utils'
import LoadingAsset from './LoadingAsset'

export function ManageUserProperties() {
  const { t } = useTranslations('property')
  const [searchInput, setSearchInput] = useState('')
  const [activeFilters, setActiveFilters] = useState<Set<AssetsManagementFilterKey>>(new Set())

  const handleFilterToggle = useCallback((key: AssetsManagementFilterKey) => {
    setActiveFilters((current) => {
      const next = new Set(current)

      if (next.has(key)) {
        next.delete(key)
      } else {
        const relationTypeFilters: AssetsManagementFilterKey[] = [
          'prospect',
          'representation',
          'reference',
        ]

        if (relationTypeFilters.includes(key)) {
          for (const relationFilter of relationTypeFilters) {
            next.delete(relationFilter)
          }
        }

        next.add(key)
      }

      return next
    })
  }, [])

  const [debouncedSearchTerm] = useDebounce(searchInput, 300)

  const { assets, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isFetching } =
    useAssetsPaginated({
      sortBy: 'created_at',
      sortDirection: 'desc',
      filters: activeFilters,
      search: debouncedSearchTerm,
    })

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const renderItem = useCallback(
    ({ item }: { item: ParsedAsset }) => (
      <PropertyListItem
        asset={item}
        showTypeBadge={false}
        className="mb-3"
        actions={[
          {
            key: 'primary',
            label: t(($) => $.actions.open),
            href: `/property/${item.id}`,
          },
        ]}
      />
    ),
    [t]
  )

  const keyExtractor = useCallback((item: ParsedAsset) => item.id, [])

  const isLoadingDebounced = useSpinDelay(isLoading || isFetching, {
    delay: 100,
    minDuration: 400,
  })

  return (
    <>
      <View className="flex-1">
        <PropertiesListHeader
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          activeFilters={activeFilters}
          onFilterToggle={handleFilterToggle}
          className="px-5 pt-6"
        />
        {isLoadingDebounced && assets.length === 0 && (
          <LoadingAsset className="mx-5 mb-20 mt-4 w-auto" />
        )}
        {!isLoadingDebounced && assets.length === 0 && <PropertiesEmptyState />}
        {!isLoadingDebounced && assets.length > 0 && (
          <AnimatedLegendList
            data={assets}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.5}
            showsVerticalScrollIndicator={false}
            className={cn(
              'flex-1',
              isLoadingDebounced ? 'opacity-0 transition-opacity duration-150' : 'opacity-100'
            )}
            contentContainerClassName="pb-18 px-3 pt-4"
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          />
        )}
      </View>
    </>
  )
}

function PropertiesListHeader({
  searchInput,
  onSearchChange,
  activeFilters,
  onFilterToggle,
  className,
}: {
  searchInput: string
  onSearchChange: (value: string) => void
  activeFilters: Set<AssetsManagementFilterKey>
  onFilterToggle: (key: AssetsManagementFilterKey) => void
  className?: string
}) {
  const { t } = useTranslations('property')

  const filterOptionsType = useMemo(
    () => [
      { key: 'prospect' as const, label: t(($) => $.relationTypes.prospect) },
      { key: 'representation' as const, label: t(($) => $.relationTypes.representation) },
      { key: 'reference' as const, label: t(($) => $.relationTypes.reference) },
    ],
    [t]
  )

  const activeTypeFilter = Array.from(activeFilters).filter((key) =>
    assetRelationTypes.includes(key as AssetRelationType)
  )[0]

  return (
    <View className={cn('gap-4', className)}>
      <View className="flex-row items-center justify-center gap-3">
        <Heading1 className="text-center font-light">{t(($) => $.list.title)}</Heading1>
      </View>
      <PropertyTypeFilter
        options={filterOptionsType}
        selected={activeTypeFilter}
        className="gap-x-3 sm:gap-x-4"
        optionVariant="outline"
        optionClassName={cn(
          'px-4 aria-selected:mx-0 aria-selected:border-accent aria-selected:bg-accent aria-selected:text-accent-foreground sm:px-6'
        )}
        labelClassName="text-sm/none"
        onChange={(key) => onFilterToggle(key as AssetRelationType)}
      />

      <Input
        value={searchInput}
        onChangeText={onSearchChange}
        placeholder={t(($) => $.list.search)}
        variant="box"
        showClearButton
        className="bg-background"
        inputClassName="text-base"
      />
    </View>
  )
}

function PropertiesEmptyState() {
  const { t } = useTranslations('property')

  return (
    <View className="flex-1 items-center justify-center gap-4 px-5 py-16 pb-32">
      <Icon name="home-outline" size={48} className="text-secondary/60" />
      <View className="items-center gap-2">
        <Text className="font-medium text-base text-primary">{t(($) => $.list.empty)}</Text>
        <Text className="text-center text-sm text-secondary">
          {t(($) => $.list.emptyDescription)}
        </Text>
      </View>
    </View>
  )
}
